import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { statusService, type UserStatus, type UserStatusInfo } from '@/services/statusService';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';

interface StatusContextType {
  // Current user status
  currentStatus: UserStatus;
  isOnline: boolean;
  updateStatus: (status: UserStatus) => Promise<void>;

  // User status lookups
  getUserStatus: (userId: string) => Promise<UserStatusInfo | null>;
  getUsersStatuses: (userIds: string[]) => Promise<UserStatusInfo[]>;

  // Status cache for frequently accessed users
  cachedStatuses: Map<string, UserStatusInfo>;
  updateCachedStatus: (userId: string, status: UserStatusInfo) => void;

  // Status change listeners
  onStatusChange: (userId: string, callback: (status: UserStatusInfo) => void) => () => void;

  // Bulk operations
  refreshStatuses: (userIds: string[]) => Promise<void>;

  // System status
  isSystemOnline: boolean;
  connectionStatus: 'connected' | 'connecting' | 'disconnected';
}

const StatusContext = createContext<StatusContextType | undefined>(undefined);

interface StatusProviderProps {
  children: ReactNode;
}

export const StatusProvider: React.FC<StatusProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [cachedStatuses, setCachedStatuses] = useState<Map<string, UserStatusInfo>>(new Map());
  const [isSystemOnline, setIsSystemOnline] = useState(navigator.onLine);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'connecting' | 'disconnected'>('connecting');

  // Initialize online status for current user
  const {
    currentStatus,
    isOnline,
    updateStatus,
    getUserStatus,
    getUsersStatuses
  } = useOnlineStatus();

  // Monitor network connectivity
  useEffect(() => {
    const handleOnline = () => {
      setIsSystemOnline(true);
      setConnectionStatus('connected');
    };

    const handleOffline = () => {
      setIsSystemOnline(false);
      setConnectionStatus('disconnected');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Update cached status with size limit to prevent memory leaks
  const updateCachedStatus = (userId: string, status: UserStatusInfo) => {
    setCachedStatuses(prev => {
      const newMap = new Map(prev);
      newMap.set(userId, status);
      
      // Limit cache size to 1000 entries to prevent memory leaks
      // Remove oldest entries if cache grows too large
      if (newMap.size > 1000) {
        const entriesToRemove = newMap.size - 1000;
        let removed = 0;
        for (const key of newMap.keys()) {
          if (removed >= entriesToRemove) break;
          newMap.delete(key);
          removed++;
        }
      }
      
      return newMap;
    });
  };

  // Listen for status changes and update cache
  useEffect(() => {
    if (!user?.id) return;

    const unsubscribe = statusService.onStatusUpdate(user.id, (statusInfo) => {
      updateCachedStatus(user.id, statusInfo);
    });

    return unsubscribe;
  }, [user?.id]);

  // Status change listener system
  const onStatusChange = (userId: string, callback: (status: UserStatusInfo) => void) => {
    // Listen for real-time status updates via statusService
    const unsubscribe = statusService.onStatusUpdate(userId, callback);

    return unsubscribe;
  };

  // Refresh multiple statuses
  const refreshStatuses = async (userIds: string[]) => {
    try {
      const statuses = await statusService.getUsersStatuses(userIds);
      const newMap = new Map(cachedStatuses);

      statuses.forEach(status => {
        newMap.set(status.id, status);
      });

      // Limit cache size to prevent memory leaks
      if (newMap.size > 1000) {
        const entriesToRemove = newMap.size - 1000;
        let removed = 0;
        for (const key of newMap.keys()) {
          if (removed >= entriesToRemove) break;
          // Don't remove statuses we just fetched
          if (!userIds.includes(key)) {
            newMap.delete(key);
            removed++;
          }
        }
      }

      setCachedStatuses(newMap);
    } catch (error) {
      console.error('Error refreshing statuses:', error);
    }
  };

  const value: StatusContextType = {
    currentStatus,
    isOnline,
    updateStatus,
    getUserStatus,
    getUsersStatuses,
    cachedStatuses,
    updateCachedStatus,
    onStatusChange,
    refreshStatuses,
    isSystemOnline,
    connectionStatus
  };

  return (
    <StatusContext.Provider value={value}>
      {children}
    </StatusContext.Provider>
  );
};

export const useStatusContext = () => {
  const context = useContext(StatusContext);
  if (context === undefined) {
    throw new Error('useStatusContext must be used within a StatusProvider');
  }
  return context;
};

